// PATH: src/pages/PaymentsPage.tsx — suivi des paiements (statut par élève ×
// période de facturation, module réservé à la direction).
//
// L'écran sert le travail réel de la direction : savoir ce qui reste à
// encaisser, retrouver les impayés, les pointer vite. D'où, dans cet ordre :
// un bandeau de totaux, des filtres (dont « impayés » d'un clic), le pointage
// en lot, puis le tableau.
//
// Les périodes sont propres aux paiements : une école facture au mois, une
// autre au trimestre. Elles ne sont plus empruntées aux trimestres du bulletin,
// qu'un rythme mensuel aurait transformés en dix bulletins par élève.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button, Card, Col, Empty, Form, Input, InputNumber, Modal, Popconfirm, Progress, Result,
  Row, Segmented, Select, Space, Statistic, Table, Tag, Typography, message,
} from "antd";
import { PlusOutlined, PrinterOutlined, SearchOutlined, SettingOutlined, WhatsAppOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { useSchoolApi } from "../api/school";
import { WHATSAPP_ENABLED } from "../config/features";
import {
  usePaymentsApi, type ClassFee, type PaymentPeriod, type PaymentRow, type PeriodKind,
} from "../api/payments";
import PageHeader from "../components/PageHeader";
import ResponsiveTable from "../components/ResponsiveTable";
import { notifyWriteError } from "../api/notify";
import { useSchoolContext } from "../hooks/useSchoolContext";
import { COLORS, FONT_MONO } from "../theme";
import type { SchoolYear } from "../models/school";

const KIND_OPTIONS: { value: PeriodKind; label: string }[] = [
  { value: "MOIS", label: "Mensuelle" },
  { value: "TRIMESTRE", label: "Trimestrielle" },
  { value: "ANNEE", label: "Annuelle" },
  { value: "LIBRE", label: "Libre" },
];

// Les montants s'alignent en colonne et se comparent : chasse fixe, séparateur
// de milliers, jamais de flottant affiché brut.
const nf = new Intl.NumberFormat("fr-MA", { maximumFractionDigits: 2 });
const formatAmount = (v: number) => `${nf.format(v)} DH`;

// Montant applicable : figé au pointage s'il existe, sinon tarif courant de la
// classe. Un élève sans classe ou sans tarif n'a pas de montant du tout.
const amountOf = (r: PaymentRow): number | undefined => r.amount_due ?? r.amount_current;

const apiError = (err: any, fallback: string) =>
  message.error(err?.response?.data?.error || fallback);

type StatusFilter = "TOUS" | "IMPAYE" | "PAYE" | "NON_POINTE";

const PaymentsPage: React.FC = () => {
  const school = useSchoolApi();
  const api = usePaymentsApi();
  const navigate = useNavigate();

  const [years, setYears] = useState<SchoolYear[]>([]);
  // Année partagée avec Pédagogie et la liste des élèves (useSchoolContext) ;
  // la période, elle, est propre à cet écran.
  const { yearId, setYearId } = useSchoolContext();
  const [periods, setPeriods] = useState<PaymentPeriod[]>([]);
  const [periodId, setPeriodId] = useState<number>();
  const [rows, setRows] = useState<PaymentRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [classFilter, setClassFilter] = useState<string>();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("TOUS");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<React.Key[]>([]);
  const [forbidden, setForbidden] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);

  useEffect(() => {
    school.listYears().then((ys) => {
      setYears(ys);
      // Année mémorisée conservée si elle existe encore, sinon la plus récente.
      setYearId((prev) => (prev && ys.some((y) => y.id === prev) ? prev : ys[0]?.id));
    }).catch(() => undefined);
  }, [school, setYearId]);

  const loadPeriods = useCallback(() => {
    if (!yearId) {
      setPeriods([]);
      setPeriodId(undefined);
      return;
    }
    api.listPeriods(yearId)
      .then((ps) => {
        setPeriods(ps);
        // Conserver la période courante si elle appartient encore à l'année.
        setPeriodId((prev) => (prev && ps.some((p) => p.id === prev) ? prev : ps[0]?.id));
      })
      .catch((err) => {
        if (err?.response?.status === 403) setForbidden(true);
      });
  }, [api, yearId]);
  useEffect(loadPeriods, [loadPeriods]);

  const load = useCallback(() => {
    if (!periodId) {
      setRows([]);
      return;
    }
    setLoading(true);
    api.list(periodId)
      .then((data) => { setRows(data); setForbidden(false); })
      .catch((err) => {
        if (err?.response?.status === 403) setForbidden(true);
        else message.error("Chargement impossible");
      })
      .finally(() => setLoading(false));
  }, [api, periodId]);
  useEffect(load, [load]);

  // Changer de période ou de filtre invalide la sélection : pointer en lot des
  // élèves qu'on ne voit plus serait un piège.
  useEffect(() => setSelected([]), [periodId, classFilter, statusFilter, search]);

  // Classes présentes dans la période affichée — dérivées des lignes, pour ne
  // proposer que des filtres qui donnent un résultat.
  const classes = useMemo(() => {
    const set = new Set<string>();
    rows.forEach((r) => r.class_label && set.add(r.class_label));
    return Array.from(set).sort();
  }, [rows]);

  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (classFilter && r.class_label !== classFilter) return false;
      if (statusFilter === "IMPAYE" && r.status !== "IMPAYE") return false;
      if (statusFilter === "PAYE" && r.status !== "PAYE") return false;
      if (statusFilter === "NON_POINTE" && r.status !== "") return false;
      if (q && !r.student_name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [rows, classFilter, statusFilter, search]);

  // Totaux sur la période ENTIÈRE, jamais sur les lignes filtrées : le bandeau
  // répond à « où en est l'encaissement ? », question qui ne dépend pas du
  // filtre affiché. Calcul côté client — le tableau est déjà chargé en entier.
  const totals = useMemo(() => {
    let expected = 0, collected = 0, unpaidCount = 0, unassigned = 0, noFee = 0;
    rows.forEach((r) => {
      const amount = amountOf(r);
      if (!r.class_label) unassigned += 1;
      else if (amount == null) noFee += 1;
      // Le compteur suit la même règle que les montants : un élève sans classe,
      // ou dans une classe sans tarif, n'a rien à encaisser. Compté hors de ce
      // bloc, il gonflait « élève(s) à encaisser » tout en étant signalé plus bas
      // comme « non facturé ».
      if (amount != null) {
        expected += amount;
        if (r.status === "PAYE") collected += amount;
        else unpaidCount += 1;
      }
    });
    return {
      expected,
      collected,
      outstanding: expected - collected,
      unpaidCount,
      unassigned,
      noFee,
      rate: expected > 0 ? Math.round((collected / expected) * 100) : 0,
    };
  }, [rows]);

  if (forbidden) {
    return <Result status="403" title="Module réservé à la direction de l'établissement" />;
  }

  // Écriture d'argent : un échec ne doit pas s'effacer tout seul au bout de
  // trois secondes. La notification reste et propose de rejouer l'opération —
  // sinon l'agent croit avoir pointé un règlement qui n'a jamais été écrit.
  // Type de retour explicite : `retry` rappelle setStatus, ce qui rend la
  // fonction auto-référente — TypeScript ne peut alors plus l'inférer.
  const setStatus = (studentId: number, status: "PAYE" | "IMPAYE"): Promise<void> =>
    api.setStatus(studentId, periodId!, status)
      .then(load)
      .catch((err) => notifyWriteError(err, {
        title: "Pointage non enregistré",
        retry: () => setStatus(studentId, status),
      }));

  // Pointage en lot : le backend n'expose pas d'écriture groupée, on enchaîne
  // les appels. Volontairement borné à la sélection visible (une classe, une
  // page) plutôt qu'à « tous les élèves ».
  const setStatusBulk = async (status: "PAYE" | "IMPAYE") => {
    const ids = selected.map(Number);
    const results = await Promise.allSettled(
      ids.map((sid) => api.setStatus(sid, periodId!, status))
    );
    const rejected = results.filter((r) => r.status === "rejected");
    if (rejected.length > 0) {
      // Les élèves en échec sont nommément rejouables : réessayer ne repointe
      // que ceux-là, pas toute la sélection.
      const failedIds = ids.filter((_, i) => results[i].status === "rejected");
      notifyWriteError((rejected[0] as PromiseRejectedResult).reason, {
        title: `${failedIds.length} pointage(s) en échec sur ${ids.length}`,
        retry: () => {
          setSelected(failedIds);
          setStatusBulk(status);
        },
      });
    } else {
      message.success(`${ids.length} élève(s) pointé(s)`);
    }
    setSelected([]);
    load();
  };

  const period = periods.find((p) => p.id === periodId);

  return (
    <div>
      <PageHeader
        title="Paiements"
        subtitle={period ? `${period.label} — ${period.year_label ?? ""}`.trim() : "Aucune période de facturation"}
        filters={
          <>
            <Select
              style={{ width: 130 }} value={yearId} onChange={setYearId} placeholder="Année"
              options={years.map((y) => ({ value: y.id, label: y.label }))}
            />
            <Select
              style={{ width: 160 }} value={periodId} onChange={setPeriodId} placeholder="Période"
              notFoundContent="Aucune période"
              options={periods.map((p) => ({ value: p.id, label: p.label }))}
            />
            <Button icon={<SettingOutlined />} disabled={!yearId} onClick={() => setConfigOpen(true)}>
              Périodes & tarifs
            </Button>
          </>
        }
        action={WHATSAPP_ENABLED ? (
          <Popconfirm
            title="Envoyer les relances WhatsApp aux impayés (cooldown 7 j) ?"
            onConfirm={() =>
              api.runReminders(periodId)
                .then((r) => { message.success(`${r.sent} relance(s) envoyée(s)`); load(); })
                .catch((err) => apiError(err, "Échec des relances"))}
          >
            <Button icon={<WhatsAppOutlined />} disabled={!periodId}>Relancer les impayés</Button>
          </Popconfirm>
        ) : undefined}
      />

      {/* Bandeau d'encaissement — la question que se pose la direction en
          ouvrant l'écran, avant même de lire une ligne. */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Attendu" value={totals.expected} formatter={(v) => formatAmount(Number(v))}
              valueStyle={{ fontFamily: FONT_MONO, fontSize: 20 }} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Encaissé" value={totals.collected} formatter={(v) => formatAmount(Number(v))}
              valueStyle={{ fontFamily: FONT_MONO, fontSize: 20, color: COLORS.accent }} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            {/* « Reste à encaisser » et non « impayé » : tant qu'un élève n'est
                pas pointé, l'école ne sait pas s'il a payé — seulement qu'elle
                ne l'a pas enregistré. */}
            <Statistic title="Reste à encaisser" value={totals.outstanding} formatter={(v) => formatAmount(Number(v))}
              valueStyle={{ fontFamily: FONT_MONO, fontSize: 20, color: totals.outstanding > 0 ? COLORS.danger : undefined }} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Typography.Text type="secondary" style={{ fontSize: 14 }}>Recouvrement</Typography.Text>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 4 }}>
              <Progress
                type="circle" size={38} percent={totals.rate}
                strokeColor={totals.rate === 100 ? COLORS.accent : COLORS.primary}
              />
              <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                {totals.unpaidCount} élève(s)<br />à encaisser
              </Typography.Text>
            </div>
          </Card>
        </Col>
      </Row>

      {(totals.unassigned > 0 || totals.noFee > 0) && (
        <Typography.Paragraph type="warning" style={{ marginBottom: 12 }}>
          {totals.unassigned > 0 && (
            <>{totals.unassigned} élève(s) sans classe — non facturés, à affecter depuis leur fiche (onglet Scolarité). </>
          )}
          {totals.noFee > 0 && (
            <>{totals.noFee} élève(s) dans une classe sans tarif pour cette période — voir « Périodes & tarifs ».</>
          )}
        </Typography.Paragraph>
      )}

      <Card
        // Filtres au-dessus du tableau, pas dans l'en-tête de page : ils
        // portent sur la liste, et le travail courant est « montre-moi les
        // impayés » — d'où un segmenté plutôt qu'un menu déroulant.
        title={
          <Space wrap size={8}>
            <Segmented
              value={statusFilter}
              onChange={(v) => setStatusFilter(v as StatusFilter)}
              options={[
                { value: "TOUS", label: "Tous" },
                { value: "IMPAYE", label: "Impayés" },
                { value: "NON_POINTE", label: "Non pointés" },
                { value: "PAYE", label: "Payés" },
              ]}
            />
            <Input
              prefix={<SearchOutlined style={{ color: "var(--app-muted)" }} />}
              placeholder="Nom de l'élève" allowClear style={{ width: 200 }}
              value={search} onChange={(e) => setSearch(e.target.value)}
            />
            <Select
              style={{ width: 160 }} value={classFilter} onChange={setClassFilter}
              placeholder="Toutes les classes" allowClear
              options={classes.map((c) => ({ value: c, label: c }))}
            />
          </Space>
        }
        extra={
          selected.length > 0 ? (
            <Space>
              <Typography.Text type="secondary">{selected.length} sélectionné(s)</Typography.Text>
              <Button size="small" type="primary" onClick={() => setStatusBulk("PAYE")}>Marquer payé</Button>
              <Button size="small" danger onClick={() => setStatusBulk("IMPAYE")}>Marquer impayé</Button>
            </Space>
          ) : null
        }
      >
        <ResponsiveTable<PaymentRow>
          rowKey="student_id" dataSource={visibleRows} loading={loading} size="small"
          rowSelection={{ selectedRowKeys: selected, onChange: setSelected, getCheckboxProps: (r) => ({ disabled: r.amount_current == null }) }}
          // Sur téléphone : qui, combien, et où en est le règlement.
          card={{
            title: (r) => r.student_name,
            extra: (r) =>
              r.status === "PAYE" ? <Tag color="success">Payé</Tag>
                : r.status === "IMPAYE" ? <Tag color="error">Impayé</Tag>
                : <Tag>Non pointé</Tag>,
            description: (r) => (
              <>
                {r.class_label && <Tag bordered={false}>{r.class_label}</Tag>}
                <Typography.Text style={{ fontFamily: FONT_MONO }}>
                  {amountOf(r) != null ? formatAmount(amountOf(r) as number) : "—"}
                </Typography.Text>
              </>
            ),
            actions: (r) => [
              <Button key="p" size="small" type="link" disabled={r.amount_current == null} onClick={() => setStatus(r.student_id, "PAYE")}>
                Payé
              </Button>,
              <Button key="i" size="small" type="link" danger disabled={r.amount_current == null} onClick={() => setStatus(r.student_id, "IMPAYE")}>
                Impayé
              </Button>,
            ],
          }}
          pagination={{ pageSize: 25, showSizeChanger: true, hideOnSinglePage: true, showTotal: (t) => `${t} élève(s)` }}
          locale={{
            emptyText: (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  !periodId ? "Créez une période de facturation"
                    : rows.length > 0 ? "Aucun élève ne correspond aux filtres"
                    : "Aucun élève inscrit sur cette période"
                } />
            ),
          }}
          columns={[
            { title: "Élève", dataIndex: "student_name", sorter: (a, b) => a.student_name.localeCompare(b.student_name),
              render: (v) => <span style={{ fontWeight: 500 }}>{v}</span> },
            { title: "Classe", dataIndex: "class_label", width: 130,
              render: (v?: string) => v ?? <Typography.Text type="secondary">non affecté</Typography.Text> },
            { title: "Montant", width: 120, align: "right",
              sorter: (a, b) => (amountOf(a) ?? -1) - (amountOf(b) ?? -1),
              render: (_, r) => {
                const amount = amountOf(r);
                if (amount == null) return <Typography.Text type="secondary">—</Typography.Text>;
                return <span style={{ fontFamily: FONT_MONO }}>{formatAmount(amount)}</span>;
              } },
            { title: "Statut", dataIndex: "status", width: 110,
              render: (s: string) =>
                s === "PAYE" ? <Tag color="green" bordered={false}>PAYÉ</Tag>
                : s === "IMPAYE" ? <Tag color="red" bordered={false}>IMPAYÉ</Tag>
                : <Tag bordered={false}>Non pointé</Tag> },
            { title: "Relances", width: 120, render: (_, r) =>
                r.reminder_count > 0
                  ? `${r.reminder_count}${r.last_reminder_at ? " (dern. " + dayjs(r.last_reminder_at).format("DD/MM") + ")" : ""}`
                  : <Typography.Text type="secondary">—</Typography.Text> },
            { title: "", width: 230, align: "right", render: (_, r) => (
              <Space>
                {/* Reçu réservé aux règlements encaissés : c'est ce qu'il
                    atteste, et le backend refuse les autres. */}
                {r.status === "PAYE" && r.enrollment_id && (
                  <Button
                    size="small" type="link" icon={<PrinterOutlined />}
                    onClick={() =>
                      navigate(`/enrollments/${r.enrollment_id}/print?doc_type=RECU_PAIEMENT&period_id=${periodId}`)}
                  >
                    Reçu
                  </Button>
                )}
                <Button size="small" type={r.status === "PAYE" ? "primary" : "default"}
                  disabled={r.amount_current == null}
                  onClick={() => setStatus(r.student_id, "PAYE")}>Payé</Button>
                <Button size="small" danger={r.status === "IMPAYE"}
                  disabled={r.amount_current == null}
                  onClick={() => setStatus(r.student_id, "IMPAYE")}>Impayé</Button>
              </Space>) },
          ]}
        />
      </Card>

      <PeriodsConfigModal
        open={configOpen}
        yearId={yearId}
        periods={periods}
        api={api}
        onClose={() => { setConfigOpen(false); loadPeriods(); load(); }}
      />
    </div>
  );
};

/**
 * PeriodsConfigModal — périodes de facturation de l'année et tarif par classe.
 *
 * Vit dans Paiements et non dans « Admin pédagogique » : y placer la
 * facturation recréerait le couplage paiements ↔ bulletin que la séparation des
 * périodes vient précisément défaire.
 */
const PeriodsConfigModal: React.FC<{
  open: boolean;
  yearId?: number;
  periods: PaymentPeriod[];
  api: ReturnType<typeof usePaymentsApi>;
  onClose: () => void;
}> = ({ open, yearId, periods, api, onClose }) => {
  const [selected, setSelected] = useState<number>();
  const [fees, setFees] = useState<ClassFee[]>([]);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const periodId = selected ?? periods[0]?.id;

  const loadFees = useCallback(() => {
    if (!periodId) { setFees([]); return; }
    setLoading(true);
    api.listFees(periodId).then(setFees).catch(() => setFees([])).finally(() => setLoading(false));
  }, [api, periodId]);
  useEffect(() => { if (open) loadFees(); }, [open, loadFees]);

  const addPeriod = (v: any) =>
    api.createPeriod({ ...v, school_year_id: yearId })
      .then(() => { form.resetFields(); message.success("Période créée"); onClose(); })
      .catch((err) => apiError(err, "Création impossible"));

  const selectedLabel = periods.find((p) => p.id === periodId)?.label;

  return (
    <Modal title="Périodes de facturation & tarifs" open={open} onCancel={onClose} footer={null} width={760} destroyOnClose>
      <Typography.Paragraph type="secondary">
        Le rythme est libre : créez douze périodes mensuelles ou trois trimestrielles. Ces périodes
        sont indépendantes des trimestres du bulletin.
      </Typography.Paragraph>

      <Form form={form} layout="inline" style={{ marginBottom: 16 }} onFinish={addPeriod}
        initialValues={{ kind: "MOIS", sort_order: (periods.length || 0) + 1 }}>
        <Form.Item name="label" rules={[{ required: true, message: "Libellé requis" }]}>
          <Input placeholder="Septembre" aria-label="Libellé de la période" style={{ width: 140 }} />
        </Form.Item>
        <Form.Item name="kind">
          <Select options={KIND_OPTIONS} aria-label="Rythme" style={{ width: 140 }} />
        </Form.Item>
        <Form.Item name="sort_order">
          <InputNumber min={1} addonBefore="Ordre" aria-label="Ordre d'affichage" style={{ width: 120 }} />
        </Form.Item>
        <Button htmlType="submit" icon={<PlusOutlined />} disabled={!yearId}>Ajouter</Button>
      </Form>

      <Table<PaymentPeriod>
        rowKey="id" dataSource={periods} pagination={false} size="small" style={{ marginBottom: 20 }}
        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Aucune période" /> }}
        rowClassName={(p) => (p.id === periodId ? "ant-table-row-selected" : "")}
        onRow={(p) => ({ onClick: () => setSelected(p.id), style: { cursor: "pointer" } })}
        columns={[
          { title: "Ordre", dataIndex: "sort_order", width: 70,
            render: (v) => <Tag bordered={false} style={{ fontFamily: FONT_MONO }}>{v}</Tag> },
          { title: "Période", dataIndex: "label", render: (v) => <span style={{ fontWeight: 500 }}>{v}</span> },
          { title: "Rythme", dataIndex: "kind", width: 120,
            render: (k: PeriodKind) => KIND_OPTIONS.find((o) => o.value === k)?.label ?? k },
          { title: "", width: 110, align: "right", render: (_, p) => (
            <Popconfirm
              title="Supprimer cette période ?"
              description="Les statuts de paiement de cette période seront supprimés."
              okText="Supprimer" cancelText="Annuler"
              onConfirm={() =>
                api.deletePeriod(p.id)
                  .then(() => { message.success("Période supprimée"); onClose(); })
                  .catch((err) => apiError(err, "Suppression impossible"))}
            >
              <Button size="small" type="text" danger>Supprimer</Button>
            </Popconfirm>) },
        ]}
      />

      <Typography.Title level={5} style={{ marginBottom: 4 }}>
        Tarif par classe {selectedLabel && <Typography.Text type="secondary">— {selectedLabel}</Typography.Text>}
      </Typography.Title>
      <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 8 }}>
        Sélectionnez une période ci-dessus pour en régler les tarifs. Un tarif à 0 n'est pas facturé.
      </Typography.Paragraph>
      <Table<ClassFee>
        rowKey="class_id" dataSource={fees} pagination={false} size="small" loading={loading}
        locale={{
          emptyText: (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={periodId ? "Aucune classe sur cette année" : "Sélectionnez une période"} />
          ),
        }}
        columns={[
          { title: "Classe", dataIndex: "class_label" },
          { title: "Montant (DH)", width: 220, render: (_, f) => (
            <InputNumber
              min={0} step={50} defaultValue={f.amount} style={{ width: 150 }}
              aria-label={`Tarif de la classe ${f.class_label}`}
              formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")}
              parser={(v) => Number(`${v}`.replace(/\s/g, "")) as any}
              // onBlur plutôt qu'onChange : une écriture par frappe enverrait
              // une requête à chaque chiffre saisi.
              onBlur={(e) => {
                const amount = Number(e.target.value.replace(/\s/g, ""));
                if (Number.isNaN(amount) || amount === f.amount) return;
                api.setFee(periodId!, f.class_id, amount)
                  .then(() => { message.success("Tarif enregistré"); loadFees(); })
                  .catch((err) => apiError(err, "Tarif refusé"));
              }}
            />) },
        ]}
      />
      <Typography.Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0, fontSize: 12 }}>
        Le montant est figé au moment du pointage : modifier un tarif ne change pas les paiements déjà pointés.
      </Typography.Paragraph>
    </Modal>
  );
};

export default PaymentsPage;
